import { Button, Grid } from "@mui/material";

import { useFormik } from "formik";

import dayjs from "dayjs";
import { useMemo, useState } from "react";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import CustomSelect from "../../../../../components/CustomSelect";
import Input from "../../../../../components/Input";
import MaskInput from "../../../../../components/MaskInput";
import MaskMultiSelectScroll from "../../../../../components/MaskMultiSelectScroll";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import AddressCommon from "../../../../../components/app/provider/patient/add-new-patient/preferences/AddressCommon";
import {
  PROVIDER_USER_INITIAL_VALUES,
  ProviderUserType,
} from "../../../../../constants/ProviderUserSchema";
import { GENDER, PROVIDER_TYPE_LIST } from "../../../../../constants/master";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useSpecialtiesList } from "../../../../../queries/use-specialities-list";
import {
  DEF_PAGINATION,
  DEFAULT_VALUE_LABEL_LIST,
  DGT_Pagination,
  getUnNecessaryTransformData,
  getXtenantId,
  showToastOnApiCall,
  transformMultiSelectFrontEndVal,
} from "../../../../../utils/utils";
import { NEW_PROVIDER_VALIDATION_SCHEMA } from "../collaboration-referral-constant";
import { Provider } from "../../../../../services";
import { useProvider } from "../../../../../queries/use-provider";
import { useUpdateProviderUser } from "../../../../../queries/use-update-provider-user";
import { useAddReferralProvider } from "../../../../../queries/use-add-provider-user";
import { useQueryClient } from "@tanstack/react-query";
import NumberInput from "../../../../../components/NumberInput";

interface NewReferringProviderProps {
  open: boolean;
  onClose: () => void;
  referralInOut: string;
  disclosureHost?: DisclosureHost;
}

const NewReferringProvider = ({
  open,
  onClose,
  referralInOut,
  disclosureHost,
}: NewReferringProviderProps) => {
  // console.log("disclosureHost", disclosureHost?.data);

  // this flow is to get the provider data from the provider api for Edit provider.
  const { data: providerData } = useProvider({
    tenantId: getXtenantId(),
    providerUuid: disclosureHost?.data,
  });

  const client = useQueryClient();
  const { mutateAsync: addReferralProvider } = useAddReferralProvider();
  const { mutateAsync: updateProvider } = useUpdateProviderUser();

  // console.log("providerData", providerData?.data);

  const showSnackbar = useSnackbar();
  const formik = useFormik<ProviderUserType>({
    initialValues: disclosureHost?.data
      ? {
          ...PROVIDER_USER_INITIAL_VALUES,
          // The record arrives in API shape: `acceptedInsurances`,
          // `licensedStates` and `workLocations` are maps rather than the
          // option lists the form type declares. This drawer neither renders
          // nor submits those three, and converts `specialities` below.
          ...(providerData?.data as unknown as Partial<ProviderUserType>),
          specialities: transformMultiSelectFrontEndVal(
            DEFAULT_VALUE_LABEL_LIST,
            providerData?.data?.specialities
          ),
        }
      : PROVIDER_USER_INITIAL_VALUES,
    validationSchema: NEW_PROVIDER_VALIDATION_SCHEMA,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  async function handleSubmit(values: ProviderUserType) {
    const transformedData = getUnNecessaryTransformData(["specialities"], {
      ...values,
      acceptedInsurances: undefined,
      licensedStates: undefined,
      workLocations: undefined,
      referralType: referralInOut ?? "",
      isReferral: true,
      npi: values?.npi ?? Number(values?.npi),
      role: "PROVIDER",
    });

    const payload = {
      // The form holds display shapes the generated model cannot describe —
      // multi-select option lists, a string npi, a Dayjs birthDate — which
      // `getUnNecessaryTransformData` and the API convert back on submit.
      providerData: transformedData as unknown as Provider,
      xTENANTID: getXtenantId(),
    };
    // console.log("payload", payload);
    if (disclosureHost?.data) showToastAndResetForm(updateProvider(payload));
    else showToastAndResetForm(addReferralProvider(payload));
  }

  async function showToastAndResetForm(apiCall: Promise<unknown>) {
    showToastOnApiCall({
      apiCall,
      showSnackbar,
      onSave: () => {
        client.invalidateQueries({
          queryKey: ["providers-List"],
        });
        client.invalidateQueries({
          queryKey: ["providers-referral-list"],
        });
        closeAndResetForm();
      },
    });
  }

  function closeAndResetForm() {
    formik.resetForm();
    disclosureHost?.close();
    onClose();
  }

  const [searchSpecialty, setSearchSpecialty] = useDebounce<string>("");
  const [specialityPagination, setSpecialityPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: specialtiesList, isFetching: specialityFetching } =
    useSpecialtiesList({
      searchString: searchSpecialty,
      pagination: specialityPagination,
      xTENANTID: getXtenantId(),
    });

  const handleSpecialtySearch = (val: string) => {
    setSearchSpecialty(val);
    setSpecialityPagination((p) => ({ ...p, page: 0 }));
  };

  const specialities = useMemo(() => {
    return specialtiesList?.data?.content?.map((it) => ({
      value: it.uuid,
      label: it?.name,
    }));
  }, [specialtiesList]);

  return (
    <SideSheetDrawer
      open={open}
      onClose={() => {
        formik.resetForm();
        onClose();
      }}
      title="Referring Provider"
      w="900px"
      footerActions={
        <Button
          size="large"
          variant="contained"
          onClick={() => formik.handleSubmit()}
        >
          Add
        </Button>
      }
    >
      <Grid container spacing={3}>
        <Grid item xs={4}>
          <Input
            required
            label="First Name"
            textFieldProps={{
              placeholder: "Enter First Name",
              onChange: formik.handleChange,
              name: "firstName",
              value: formik.values.firstName,
            }}
            error={formik.touched.firstName && formik.errors.firstName}
          />
        </Grid>
        <Grid item xs={4}>
          <Input
            required
            label="Last Name"
            textFieldProps={{
              placeholder: "Enter Last Name",
              onChange: formik.handleChange,
              name: "lastName",
              value: formik.values.lastName,
            }}
            error={formik.touched.lastName && formik.errors.lastName}
          />
        </Grid>
        <Grid item xs={4}>
          <CustomSelect
            label="Provider Type"
            options={PROVIDER_TYPE_LIST}
            value={formik.values.providerType}
            onChange={(value) => {
              formik.setFieldValue("providerType", value ?? undefined);
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <CustomDatePicker
            maxDate={dayjs(new Date())}
            label="Date of Birth"
            value={formik.values.birthDate}
            onChange={(val) => {
              formik.setFieldValue("birthDate", val);
            }}
            error={formik.touched?.birthDate && formik.errors?.birthDate}
          />
        </Grid>
        <Grid item xs={4}>
          <CustomSelect
            name="gender"
            required
            label="Gender"
            options={GENDER}
            value={formik.values.gender}
            onChange={(val) => {
              formik.setFieldValue("gender", val);
            }}
            error={formik.touched.gender && formik.errors.gender}
          />
        </Grid>
        <Grid item xs={4}>
          <Input
            required
            label="Email"
            textFieldProps={{
              name: "email",
              placeholder: "Enter Email",
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              value: formik.values.email,
            }}
            error={formik.touched?.email && formik.errors?.email}
          />
        </Grid>
        <Grid item xs={4}>
          <MaskInput
            mask={"(999) 999-9999"}
            label="Mobile Number"
            placeholder="Enter Mobile Number"
            value={formik.values?.phone}
            onChange={(e) => formik.setFieldValue("phone", e.target.value)}
          />
        </Grid>
        <Grid item xs={4}>
          <MaskMultiSelectScroll
            label="Specialty"
            onSearch={handleSpecialtySearch}
            optionWithCheckbox={true}
            value={formik.values?.specialities}
            placeholder="Search & Select Specialities"
            options={specialities}
            onChange={(val) => formik.setFieldValue("specialities", val)}
            pagination={specialityPagination}
            setPagination={setSpecialityPagination}
            totalElements={specialtiesList?.data?.page?.totalElements}
            loading={specialityFetching}
          />
        </Grid>
        <Grid item xs={4}>
          <Input
            required
            label="Practice Name"
            textFieldProps={{
              placeholder: "Enter Practice Name",
              onChange: formik.handleChange,
              name: "practiceName",
              value: formik.values.practiceName,
            }}
            error={formik.touched.practiceName && formik.errors.practiceName}
          />
        </Grid>
        <AddressCommon formik={formik} formikKey={`address`} />
        <Grid item xs={4}>
          <NumberInput
            label="NPI Number"
            textFieldProps={{
              placeholder: "Enter NPI Number",
              onChange: formik.handleChange,
              name: "npi",
              value: formik.values.npi,
            }}
            error={formik.touched.npi && formik.errors.npi}
          />
        </Grid>
      </Grid>
    </SideSheetDrawer>
  );
};

export default NewReferringProvider;
